/**
 * iOS-safe long-press helper.
 *
 * Returns a cleanup function. Suppresses the default context menu,
 * cancels on scroll-threshold movement so a gentle drag doesn't fire
 * a press, and works with mouse + touch + pointer events.
 *
 * Usage:
 *   const cleanup = longPress(node, () => openContextMenu(node));
 *   // later: cleanup();
 *
 * @param {HTMLElement} element
 * @param {(el: HTMLElement) => void} handler
 * @param {{ duration?: number, moveThreshold?: number }} [opts]
 * @returns {() => void} cleanup
 */
export function longPress(element, handler, opts = {}) {
  const { duration = 500, moveThreshold = 10 } = opts;

  let timerId = null;
  let startX = 0;
  let startY = 0;

  const clear = () => {
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
  };

  const onStart = (x, y) => {
    clear();
    startX = x;
    startY = y;
    timerId = setTimeout(() => {
      timerId = null;
      handler(element);
    }, duration);
  };

  const onMove = (x, y) => {
    if (timerId === null) return;
    const dx = Math.abs(x - startX);
    const dy = Math.abs(y - startY);
    if (dx > moveThreshold || dy > moveThreshold) {
      clear();
    }
  };

  const pointerDown = (e) => {
    // Only primary button for mouse; touch/pointer always proceeds.
    if (e.button !== undefined && e.button !== 0) return;
    const x = e.clientX ?? (e.touches && e.touches[0] && e.touches[0].clientX) ?? 0;
    const y = e.clientY ?? (e.touches && e.touches[0] && e.touches[0].clientY) ?? 0;
    onStart(x, y);
  };

  const pointerMove = (e) => {
    const x = e.clientX ?? (e.touches && e.touches[0] && e.touches[0].clientX) ?? 0;
    const y = e.clientY ?? (e.touches && e.touches[0] && e.touches[0].clientY) ?? 0;
    onMove(x, y);
  };

  const pointerEnd = () => clear();

  const suppressContextMenu = (e) => {
    // Prevent iOS Safari from showing the system context menu during our press.
    e.preventDefault();
  };

  element.addEventListener('pointerdown', pointerDown);
  element.addEventListener('pointermove', pointerMove);
  element.addEventListener('pointerup', pointerEnd);
  element.addEventListener('pointercancel', pointerEnd);
  element.addEventListener('pointerleave', pointerEnd);
  element.addEventListener('contextmenu', suppressContextMenu);

  return () => {
    clear();
    element.removeEventListener('pointerdown', pointerDown);
    element.removeEventListener('pointermove', pointerMove);
    element.removeEventListener('pointerup', pointerEnd);
    element.removeEventListener('pointercancel', pointerEnd);
    element.removeEventListener('pointerleave', pointerEnd);
    element.removeEventListener('contextmenu', suppressContextMenu);
  };
}
